"use client";

import { motion } from "framer-motion";
import Image from "next/image";

const defaultItems = [
  {
    image: "/recycling-bin.webp",
    name: "Återvinning",
    desc: "Glas, metall, papper och annat återvinningsbart",
  },
  {
    image: "/box.webp",
    name: "Kartonger",
    desc: "Flyttkartonger och wellpappkartonger",
  },
  {
    image: "/furniture.webp",
    name: "Möbler",
    desc: "Vardagsrum, sovrum, kontor och andra hemmöbler",
  },
  {
    image: "/electronics.webp",
    name: "Elektronik",
    desc: "TV-apparater, datorer, ljudsystem och annan elektronik",
  },
  {
    image: "/appliances.webp",
    name: "Vitvaror",
    desc: "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
  },
  {
    image: "/fitness-equipment.webp",
    name: "Träningsutrustning",
    desc: "Löpband, cyklar, vikter och annan träningsutrustning",
  },
];

import type { Locale } from "./templates/ServicePageTemplate";

interface ServiceCategoriesProps {
  heading?: string;
  items?: { image?: string; name: string; desc: string; alt?: string }[];
  locale?: Locale;
}

export default function ServiceCategories({
  heading = "Saker vi bortforslar",
  items = defaultItems,
  locale = "sv",
}: ServiceCategoriesProps = {}) {
  return (
    <section id="services" className="py-8 sm:pt-10 sm:pb-24 bg-[#F5F5F5]">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark mb-10 sm:mb-14"
        >
          {heading}
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8 sm:gap-y-10">
          {items.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.07 }}
              className="flex items-center gap-5"
            >
              {item.image ? (
                <div className="relative h-16 w-16 sm:h-[72px] sm:w-[72px] flex-shrink-0">
                  <Image
                    src={item.image}
                    alt={item.alt ?? item.name}
                    fill
                    sizes="72px"
                    unoptimized
                    className="object-contain"
                  />
                </div>
              ) : (
                <div className="h-16 w-16 sm:h-[72px] sm:w-[72px] flex-shrink-0 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-primary">
                    {item.name.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h3 className="font-bold text-base sm:text-lg text-brand-dark">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5 leading-snug">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.a
          href="#alla-tjanster"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-10 inline-block text-sm font-medium text-brand-secondary underline underline-offset-4 cursor-pointer hover:opacity-80 transition-opacity"
        >
          {locale === "en" ? "And much more!" : "Och mycket mer!"}
        </motion.a>
      </div>
    </section>
  );
}
